import plansService from "./plansService";
import type { Plan } from "./plansService";

export interface FeaturePlanInfo {
  featureName: string;
  requiredPlan: string;
  requiredPlanPrice: number;
  allPlansWithFeature: Plan[];
}

class FeatureService {
  private plansCache: Plan[] | null = null;
  private cacheTimestamp: number | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all plans with caching
   */
  private async getPlans(): Promise<Plan[]> {
    const now = Date.now();

    // Return cached plans if cache is still valid
    if (
      this.plansCache &&
      this.cacheTimestamp &&
      now - this.cacheTimestamp < this.CACHE_DURATION
    ) {
      return this.plansCache;
    }

    try {
      const plans = await plansService.getPlans();
      this.plansCache = plans;
      this.cacheTimestamp = now;
      return plans;
    } catch (error) {
      console.error("Error fetching plans for feature service:", error);
      // Return cached plans if available, even if expired
      return this.plansCache || [];
    }
  }

  /**
   * Get the lowest plan that includes a specific feature
   */
  async getRequiredPlanForFeature(
    feature: string,
  ): Promise<FeaturePlanInfo | null> {
    try {
      const plans = await this.getPlans();

      // Find all plans that include this feature
      const plansWithFeature = plans.filter(
        (plan) =>
          plan.enabledFeatures && plan.enabledFeatures.includes(feature),
      );

      if (plansWithFeature.length === 0) {
        console.warn(`No plans found with feature: ${feature}`);
        return null;
      }

      // Sort by displayOrder to get the lowest plan
      const sortedPlans = plansWithFeature.sort(
        (a, b) => a.displayOrder - b.displayOrder,
      );
      const requiredPlan = sortedPlans[0];

      return {
        featureName: this.getFeatureDisplayName(feature),
        requiredPlan: requiredPlan.name,
        requiredPlanPrice: requiredPlan.price,
        allPlansWithFeature: sortedPlans,
      };
    } catch (error) {
      console.error("Error getting required plan for feature:", error);
      return null;
    }
  }

  /**
   * Get display name for a feature
   */
  private getFeatureDisplayName(feature: string): string {
    const featureNames: Record<string, string> = {
      code_generation: "Génération de code",
      variable_library: "Bibliothèque de variables",
      code_editor: "Éditeur de code",
      export_functionality: "Fonctionnalités d'export",
    };

    return featureNames[feature] || "Fonctionnalité premium";
  }

  /**
   * Clear cache (useful for testing or when plans are updated)
   */
  clearCache(): void {
    this.plansCache = null;
    this.cacheTimestamp = null;
  }
}

export const featureService = new FeatureService();
